import { createPortal, useFrame } from '@react-three/fiber';
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as THREE from 'three';
import type { RuleSet } from '@/game/rules';
import { maxLevel } from '@/game/sim';
import {
  AnimateContext,
  BuildingModel,
  FenceSection,
  FootprintTileModel,
  SPRAWL_VARIANTS,
  SprawlTileModel,
  UnitModel,
  WatchSpotMarker,
} from './models';
import { BAKE_ACCENT, BAKE_GLOW, bakeParts } from './models/bake';
import { ModelBatcher } from './models/batch';

/**
 * The React side of instanced models (`models/batch.ts`). Wrap the layers in
 * `<ModelBatcherProvider>`; a layer takes the shared batcher with
 * `useModelBatcher()` and, each frame or tick, calls
 * `batcher.set(entityId, look, placeMatrix(...), ownerColour)` for what it
 * shows and `batcher.remove(entityId)` for what it no longer does. Looks are
 * baked from the JSX registry on first use (a few per frame, through an
 * off-screen portal), and the `prebake` looks ahead of time while the page is
 * idle.
 */

export { ModelBatcher } from './models/batch';

/** A look key: what to mount to bake it. Levels and variants are part of the key. */
export const unitLook = (type: string) => `unit:${type}`;
export const buildingLook = (type: string, level: number) => `building:${type}:${level}`;
/** An extra tile of a grown building's footprint (at the building's level). */
export const footprintLook = (type: string, level: number) => `footprint:${type}:${level}`;
export const sprawlLook = (level: number, variant: number) => `sprawl:${level}:${variant}`;
/** A fence section `length` long along local x, centred on its origin. */
export const fenceLook = (length: number, height = 1) => `fence:${length}:${height}`;
/** A watchtower's watch spot marker (owner only). */
export const WATCH_SPOT_LOOK = 'watch';

/**
 * Every look the rules can show, most common first (units, then buildings at
 * L1, then higher levels, footprint tiles and sprawl), for idle pre-baking.
 * `extra` adds layer-specific looks (fence sections).
 */
export function prebakeLooks(rules: RuleSet, extra: readonly string[] = []): string[] {
  const looks = Object.keys(rules.units).map(unitLook);
  const buildings = Object.entries(rules.buildings);
  for (const [type] of buildings) looks.push(buildingLook(type, 1));
  looks.push(...extra);
  for (const [type, def] of buildings) {
    for (let level = 2; level <= maxLevel(def); level++) looks.push(buildingLook(type, level));
  }
  for (const [type, def] of buildings) {
    const extraTiles = def.growth?.extraTilesByLevel ?? [];
    extraTiles.forEach((n, i) => {
      if (n > 0) looks.push(footprintLook(type, i + 1));
    });
  }
  for (const [level, count] of Object.entries(SPRAWL_VARIANTS)) {
    for (let variant = 0; variant < count; variant++)
      looks.push(sprawlLook(Number(level), variant));
  }
  return looks;
}

const UP = new THREE.Vector3(0, 1, 0);
const tmpPosition = new THREE.Vector3();
const tmpQuaternion = new THREE.Quaternion();
const tmpScale = new THREE.Vector3();

/**
 * An entity's matrix: at (x, y, z), turned `yaw` about y, its height scaled
 * by `scaleY` (a construction site rising from the ground).
 */
export function placeMatrix(
  out: THREE.Matrix4,
  x: number,
  y: number,
  z: number,
  yaw = 0,
  scaleY = 1
): THREE.Matrix4 {
  return out.compose(
    tmpPosition.set(x, y, z),
    tmpQuaternion.setFromAxisAngle(UP, yaw),
    tmpScale.set(1, scaleY, 1)
  );
}

/** What a look key mounts, with the bake placeholders for the owner's colours. */
function LookModel({ look }: { look: string }) {
  const [kind, a, b] = look.split(':');
  const colours = { accent: BAKE_ACCENT, glow: BAKE_GLOW };
  switch (kind) {
    case 'unit':
      return <UnitModel type={a} level={1} {...colours} />;
    case 'building':
      return <BuildingModel type={a} level={Number(b)} {...colours} />;
    case 'footprint':
      return <FootprintTileModel type={a} level={Number(b)} {...colours} />;
    case 'sprawl':
      return <SprawlTileModel level={Number(a)} variant={Number(b)} {...colours} />;
    case 'fence':
      return <FenceSection x={0} z={0} yaw={0} length={Number(a)} height={Number(b)} />;
    case 'watch':
      return <WatchSpotMarker {...colours} />;
    default:
      console.warn(`ModelBatcher: unknown look ${look}`);
      return null;
  }
}

/** Mounts one look and hands its parts to the batcher once it's in the tree. */
function BakeLook({ look, batcher }: { look: string; batcher: ModelBatcher }) {
  const root = useRef<THREE.Group>(null);
  useLayoutEffect(() => {
    if (root.current) batcher.defineLook(look, bakeParts(root.current));
  }, [look, batcher]);
  return (
    <group ref={root}>
      <LookModel look={look} />
    </group>
  );
}

/** Looks baked per frame while entities wait on them (~2 ms each). */
const BAKES_PER_FRAME = 4;

function Baker({ batcher, prebake }: { batcher: ModelBatcher; prebake: readonly string[] }) {
  // Off-screen: never rendered, only mounted so the looks' trees can be read.
  const scene = useMemo(() => new THREE.Scene(), []);
  const [baking, setBaking] = useState<string[]>([]);
  useFrame(() => {
    // Nothing waiting (most of a match): no arrays, just unmount the last bakes.
    if (batcher.wantedCount === 0) {
      if (baking.length > 0) setBaking([]);
      return;
    }
    const next = batcher.wantedLooks().slice(0, BAKES_PER_FRAME);
    // Unmount finished looks; mount the next ones.
    if (next.length !== baking.length || next.some((look, i) => look !== baking[i])) {
      setBaking(next);
    }
  });
  // Pre-bake one look per idle period, so the first of each costs no frame later.
  useEffect(() => {
    const queue = prebake.filter((look) => !batcher.hasLook(look));
    if (queue.length === 0) return;
    const idle =
      typeof requestIdleCallback === 'function'
        ? (fn: () => void) => requestIdleCallback(fn, { timeout: 2000 })
        : (fn: () => void) => setTimeout(fn, 50);
    const cancel = typeof cancelIdleCallback === 'function' ? cancelIdleCallback : clearTimeout;
    let handle = 0;
    const step = () => {
      // Wait while a bake is in flight, so idle work never stacks up.
      if (batcher.wantedCount === 0) {
        const look = queue.shift();
        if (look !== undefined) batcher.want(look);
      }
      if (queue.length > 0) handle = Number(idle(step));
    };
    handle = Number(idle(step));
    return () => cancel(handle);
  }, [batcher, prebake]);
  return createPortal(
    <AnimateContext.Provider value={false}>
      {baking.map((look) => (
        <BakeLook key={look} look={look} batcher={batcher} />
      ))}
    </AnimateContext.Provider>,
    scene
  );
}

const ModelBatcherContext = createContext<ModelBatcher | null>(null);

/** The shared batcher of the enclosing `ModelBatcherProvider`. */
export function useModelBatcher(): ModelBatcher {
  const batcher = useContext(ModelBatcherContext);
  if (!batcher) throw new Error('useModelBatcher needs a ModelBatcherProvider');
  return batcher;
}

/**
 * One batcher for the layers inside: its meshes, its baker, and the per-frame
 * update (matrices the layers changed, the animation clock). The layers'
 * `useFrame`s run before its own, as children subscribe first.
 */
export function ModelBatcherProvider({
  prebake = NO_LOOKS,
  children,
}: {
  prebake?: readonly string[];
  children?: ReactNode;
}) {
  const batcher = useMemo(() => new ModelBatcher(), []);
  useEffect(() => () => batcher.dispose(), [batcher]);
  useFrame(({ clock }) => {
    batcher.update(clock.elapsedTime);
  });
  return (
    <ModelBatcherContext.Provider value={batcher}>
      <primitive object={batcher.group} />
      <Baker batcher={batcher} prebake={prebake} />
      {children}
    </ModelBatcherContext.Provider>
  );
}

const NO_LOOKS: readonly string[] = [];
